// mergeServiceWorkerMessages.js
'use strict';
import { BATCHED_PUSHER_EVENT_TYPES } from '../resource/batchPusherEvents.js';
import { PUSHER_EVENT_GOT } from '../serviceWorker/SWMessageLocalTypes.js';

// Log-only messages with no ordering dependency. They come paired with
// every pusher event, so they must not break the adjacency of the real
// events around them.
const TRANSPARENT_MESSAGE_TYPES = [PUSHER_EVENT_GOT];

/**
 * Merge adjacent messages of the same batched pusher event type and stream
 * by concatenating their batchedData. Only adjacent messages merge, so
 * ordering across different event types is preserved.
 * @param {object} params
 * @param {[object]} params.messages - FIFO messages of shape { type, payload }.
 * @returns {[object]} Merged messages in arrival order.
 * @example
 * mergeServiceWorkerMessages({
 *   messages: [
 *     { type: 'gift.sent', payload: { streamId: 'a', batchedData: [1] } },
 *     { type: 'gift.sent', payload: { streamId: 'a', batchedData: [2] } },
 *   ],
 * });
 * // => [{ type: 'gift.sent', payload: { streamId: 'a', batchedData: [1, 2] } }]
 */
const mergeServiceWorkerMessages = ({ messages = [] } = {}) => {
  const mergedMessages = [];
  let candidateIndex = -1;

  messages.forEach(message => {
    if (TRANSPARENT_MESSAGE_TYPES.includes(message?.type)) {
      mergedMessages.push(message);
      return;
    }

    const candidate = mergedMessages[candidateIndex];
    const isMergeableWithCandidate =
      candidate &&
      candidate.type === message?.type &&
      BATCHED_PUSHER_EVENT_TYPES.includes(message.type) &&
      candidate.payload?.streamId === message.payload?.streamId &&
      Array.isArray(candidate.payload?.batchedData) &&
      Array.isArray(message.payload?.batchedData);

    if (isMergeableWithCandidate) {
      mergedMessages[candidateIndex] = {
        ...candidate,
        payload: {
          ...candidate.payload,
          batchedData: [
            ...candidate.payload.batchedData,
            ...message.payload.batchedData,
          ],
        },
      };
      return;
    }

    mergedMessages.push(message);
    candidateIndex = mergedMessages.length - 1;
  });

  return mergedMessages;
};

export default mergeServiceWorkerMessages;
